import './data-mapping-page.css';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Dropdown, Input, notification, Select, Space, Spin, Tree } from "antd";
import { AiOutlineDown } from "react-icons/ai";
import { FaCircle, FaCircleDot, FaCircleExclamation, FaElementor, FaHashnode  } from "react-icons/fa6";
import { MdOutlineDataArray } from "react-icons/md";
import { TbHexagonNumber1Filled } from 'react-icons/tb';
import { RiNumber1 } from "react-icons/ri";
import { RxComponentBoolean, RxText, RxTextAlignJustify } from "react-icons/rx";
import { VscSymbolBoolean } from "react-icons/vsc";


import { Client } from '../../model/client-model';
import { TdiNodeModel } from '../../model/tdi-node-model';
import { DataMapModel } from '../../model/data-map-model';

import { useLazyGetDataFieldsQuery, useLazyGetDataMapQuery, useSaveDataMapMutation } from '../../redux/api/data-maps';
import { useLazyGetClientQuery } from '../../redux/api/clients';
import { ConverterModal } from './converter-modal';
import { FaSearch } from 'react-icons/fa';
import { PreviewModal } from './preview-modal';

import { ActionType, attributeItems, nodeItems, NodeType, leafNodeItems, cdataItems } from './node-items';

interface Props { }

const DataMappingPage: React.FC<Props> = (): JSX.Element => {

    const [loading, setLoading] = useState<boolean>();
    const [dataMapFields, setDataMapFields] = useState([]);
    const [treeData, setTreeData] = useState<TdiNodeModel[]>([]);
    const [currentNodeModel, setCurrentNodeModel] = useState<TdiNodeModel>();
    const [client, setClient] = useState<Client>();
    const [edited, setEdited] = useState<boolean>(false);
    const [isConverterOpen, setConverterOpen] = useState<boolean>(false);
    const [isPreviewOpen, setPreviewOpen] = useState<boolean>(false);

    const { clientId } = useParams();

    const [getClient] = useLazyGetClientQuery();
    const [getDataMapFields] = useLazyGetDataFieldsQuery();
    const [getDataMap] = useLazyGetDataMapQuery();
    const [saveDataMap] = useSaveDataMapMutation();

    useEffect(() => {

        const fetchDataMapFields = async () => {

            try {
                setLoading(true);
                const data = await getDataMapFields(clientId).unwrap();
                let options = data.map((field: string) => {
                    return { value: field, label: field };
                })
                options.unshift({value: '', label: ''});
                setDataMapFields(options);
                setLoading(false);
            } catch (e) {
                console.log(e);
                notification.error({
                    message: "Data Fields Error!",
                    description: `An error occurred while fetching the trusted driver data fields.`
                });
            }
        }

        const fetchClient = async () => {
            try {
                const data = await getClient(clientId).unwrap();
                setClient(data);
            } catch (e) {
                console.log(e);
                notification.error({
                    message: "Clients Error!",
                    description: `An error occurred while fetching the clients.`
                });
            }
        }

        fetchDataMapFields();
        fetchClient();

    }, []);

    useEffect(() => {
        
        const fetchDataMaps = async () => {

            const initializeTree = (nodes?: DataMapModel[]): TdiNodeModel[] => {

                const children = nodes?.map((item: DataMapModel) => {
                    let dataMap = null;
                    switch (item?.type) {
                        case NodeType.ARRAY_NODE: dataMap = createArrayNode(undefined, item); break;
                        case NodeType.CDATA: dataMap = createCData(undefined, item); break;
                        case NodeType.ATTRIBUTE: dataMap = createAttribute(undefined, item); break;
                        case NodeType.MAPPED_ATTRIBUTE: dataMap = createMappedAttribute(undefined, item); break;
                        case NodeType.SEQUENCE_ATTRIBUTE: dataMap = createSequenceAttribute(undefined, item); break;
                        case NodeType.STATIC_BOOLEAN: dataMap = createStaticBooleanNode(undefined, item); break;
                        case NodeType.MAPPED_BOOLEAN: dataMap = createMappedBooleanNode(undefined, item); break;
                        case NodeType.STATIC_NUMBER: dataMap = createStaticNumberNode(undefined, item); break;
                        case NodeType.MAPPED_NUMBER: dataMap = createMappedNumberNode(undefined, item); break;
                        case NodeType.SEQUENCE_NUMBER: dataMap = createSequenceNumberNode(undefined, item); break;
                        case NodeType.STATIC_TEXT: dataMap = createStaticTextNode(undefined, item); break;
                        case NodeType.MAPPED_TEXT: dataMap = createMappedTextNode(undefined, item); break;
                        case NodeType.SEQUENCE_TEXT: dataMap = createSequenceTextNode(undefined, item); break;
                        case NodeType.PHANTOM_ARRAY_NODE: dataMap = createPhantomArrayNode(undefined, item); break;
                        case NodeType.PHANTOM_NODE: dataMap = createPhantomNode(undefined, item); break;
                        default: dataMap = createNode(undefined, item)
                    }
                    dataMap.children = item.children ? initializeTree(item.children) : [];
                    return dataMap;
                });
                return children ? children : [];
            }

            try {
                const dataMap = await getDataMap(clientId).unwrap();
                if (dataMap && dataMap.children && dataMap.children.length > 0) {
                    const data = initializeTree(dataMap.children);
                    setTreeData(data);
                }
            } catch (e) {
                console.log(e);
                notification.error({
                    message: "Data Map Configuration Error!",
                    description: `An error occurred while fetching the data map configuration.`
                });
            }
        }

        if (dataMapFields.length !== 0) {
            fetchDataMaps();
        }

    }, [dataMapFields])

    /**
     * Recursive function to traverse the tree node and find the node equivalent to
     * the node.
     * 
     * @param node the node find
     * @param nodes the tree nodes
     * @returns 
     */
    //@ts-ignore
    const findNodeFromTree = (node: TdiNodeModel, nodes: TdiNodeModel[]): TdiNodeModel => {
        for (let i = 0; i < nodes.length; i++) {
            let item = nodes[i];
            if (item.key.toString() === node.key.toString()) {
                return item;
            }
            if (item.children) {
                let foundItem = findNodeFromTree(node, item.children);
                if (foundItem) {
                    return foundItem;
                }
            }
        }
    }

    /**
     * Recursive function to traverse the tree node and find the parent node given the node's
     * parent key.
     */

    //@ts-ignore
    const findParentFromTree = (parentKey?: string, nodes: TdiNodeModel[]): TdiNodeModel => {
        for (let i = 0; i < nodes.length; i++) {
            let item = nodes[i];
            if (item.key.toString() === parentKey) {
                return item;
            }
            if (item.children) {
                let foundItem = findParentFromTree(parentKey, item.children);
                if (foundItem) {
                    return foundItem;
                }
            }
        }
    }

    /**
     * Adds a child node to the parent node
     * @param node the parent node
     * @param childNode the node to add
     */
    const addToParentNode = (parentNode: TdiNodeModel, childNode: TdiNodeModel) => {

        setTreeData((tree) => {
            let clonedTree = [...tree];
            let children = parentNode.children ? [...parentNode.children, childNode] : [childNode];
            let foundNode: TdiNodeModel = findNodeFromTree(parentNode, clonedTree);
            if (foundNode) {
                foundNode.children = children;
            }
            return clonedTree;
        });
    }

    /**
     * Add a node above the selected node.
     * 
     * @param selectedNode the selected node
     * @param siblingNode the sibling node to add above
     */
    const addNodeAbove = (selectedNode: TdiNodeModel, siblingNode: TdiNodeModel) => {

        setTreeData((tree) => {
            let clonedTree = [...tree];
            let parentNode: TdiNodeModel = findParentFromTree(selectedNode.parentKey, clonedTree);
            if (parentNode) {
                let children = [...parentNode.children];
                const ndx = children.findIndex((child) => {
                    return child.key.toString() === selectedNode.key.toString()
                });
                children.splice(ndx, 0, siblingNode);
                parentNode.children = children;
                return clonedTree;
            } else {
                let clonedTree = [...tree];
                const ndx = clonedTree.findIndex((child) => child.key.toString() === selectedNode.key.toString());
                clonedTree.splice(ndx, 0, siblingNode);
                return clonedTree;
            }
        });
    }

    /**
     * Add a node below the selected node.
     * 
     * @param selectedNode the selected node
     * @param siblingNode the sibling node to add below
     */
    const addNodeBelow = (selectedNode: TdiNodeModel, siblingNode: TdiNodeModel) => {

        setTreeData((tree) => {
            let clonedTree = [...tree];
            let parentNode: TdiNodeModel = findParentFromTree(selectedNode.parentKey, clonedTree);
            if (parentNode) {
                let children = [...parentNode.children];
                const ndx = children.findIndex((child) => {
                    return child.key.toString() === selectedNode.key.toString()
                });
                if (ndx !== children.length -1) {
                    children.splice(ndx + 1, 0, siblingNode);
                } else {
                    children.push(siblingNode);
                }
                parentNode.children = children;
                return clonedTree;
            } else {
                let clonedTree = [...tree];
                const ndx = clonedTree.findIndex((child) => child.key.toString() === selectedNode.key.toString());
                if (ndx !== clonedTree.length - 1) {
                    clonedTree.splice(ndx + 1, 0, siblingNode);
                } else {
                    clonedTree.push(siblingNode);
                }
                return clonedTree;
            }
        });
    }

    /**
     * Handles a node action whether to add a node, add a text node, or to delete a node.
     * 
     * @param menuKey the identifier of the menu item that received a click.
     * @param node the node associated with the action.
     */
    const handleNodeAction = (menuKey: string, node: TdiNodeModel) => {

        switch (menuKey) {

            case ActionType.ADD_CHILD_NODE:
                const childNode = createNode(node.key.toString());
                addToParentNode(node, childNode);
                break;

            case ActionType.ADD_CHILD_CDATA:
                const childCData = createCData(node.key.toString());
                addToParentNode(node, childCData);
                break;

            case ActionType.ADD_CHILD_ARRAY_NODE:
                const childArrayNode = createArrayNode(node.key.toString());
                addToParentNode(node, childArrayNode);
                break;

            case ActionType.ADD_CHILD_PHANTOM_ARRAY_NODE:
                const childPhantomArrayNode = createPhantomArrayNode(node.key.toString());
                addToParentNode(node, childPhantomArrayNode);
                break;

            case ActionType.ADD_CHILD_PHANTOM_NODE:
                const childPhantomNode = createPhantomNode(node.key.toString());
                addToParentNode(node, childPhantomNode);
                break;

            case ActionType.ADD_CHILD_MAPPED_BOOLEAN:
                const childMappedBooleanNode = createMappedBooleanNode(node.key.toString());
                addToParentNode(node, childMappedBooleanNode);
                break;

            case ActionType.ADD_CHILD_STATIC_BOOLEAN:
                const childStaticBooleanNode = createStaticBooleanNode(node.key.toString());
                addToParentNode(node, childStaticBooleanNode);
                break;

            case ActionType.ADD_CHILD_SEQUENCE_NUMBER:
                const childSequenceNumberNode = createSequenceNumberNode(node.key.toString());
                addToParentNode(node, childSequenceNumberNode);
                break;

            case ActionType.ADD_CHILD_MAPPED_NUMBER:
                const childMappedNumberNode = createMappedNumberNode(node.key.toString());
                addToParentNode(node, childMappedNumberNode);
                break;

            case ActionType.ADD_CHILD_STATIC_NUMBER:
                const childStaticNumberNode = createStaticNumberNode(node.key.toString());
                addToParentNode(node, childStaticNumberNode);
                break;

            case ActionType.ADD_CHILD_SEQUENCE_TEXT:
                const childSequenceTextNode = createSequenceTextNode(node.key.toString());
                addToParentNode(node, childSequenceTextNode);
                break;

            case ActionType.ADD_CHILD_MAPPED_TEXT:
                const childMappedTextNode = createMappedTextNode(node.key.toString());
                addToParentNode(node, childMappedTextNode);
                break;

            case ActionType.ADD_CHILD_STATIC_TEXT:
                const childStaticTextNode = createStaticTextNode(node.key.toString());
                addToParentNode(node, childStaticTextNode);
                break;

            case ActionType.ADD_ATTRIBUTE:
                const childAttribute = createAttribute(node.key.toString());
                addToParentNode(node, childAttribute);
                break;
                    
            case ActionType.ADD_MAPPED_ATTRIBUTE:
                const childMappedAttribute = createMappedAttribute(node.key.toString());
                addToParentNode(node, childMappedAttribute);
                break;
    
            case ActionType.ADD_SEQUENCE_ATTRIBUTE:
                const childSequenceAttribute = createSequenceAttribute(node.key.toString());
                addToParentNode(node, childSequenceAttribute);
                break;

            case ActionType.ADD_SIBLING_NODE_ABOVE:
                const siblingNodeAbove = createNode(node.parentKey?.toString());
                addNodeAbove(node, siblingNodeAbove);
                break;

            case ActionType.ADD_SIBLING_CDATA_ABOVE:
                const siblingCDataAbove = createCData(node.parentKey?.toString());
                addNodeAbove(node, siblingCDataAbove);
                break;

            case ActionType.ADD_SIBLING_ARRAY_NODE_ABOVE:
                const siblingArrayNodeAbove = createArrayNode(node.parentKey?.toString());
                addNodeAbove(node, siblingArrayNodeAbove);
                break;

            case ActionType.ADD_SIBLING_PHANTOM_NODE_ABOVE:
                const siblingPhantomNodeAbove = createPhantomNode(node.parentKey?.toString());
                addNodeAbove(node, siblingPhantomNodeAbove);
                break;

            case ActionType.ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE:
                const siblingPhantomArrayNodeAbove = createPhantomArrayNode(node.parentKey?.toString());
                addNodeAbove(node, siblingPhantomArrayNodeAbove);
                break;

            case ActionType.ADD_SIBLING_STATIC_BOOLEAN_ABOVE:
                const siblingStaticBooleanAbove = createStaticBooleanNode(node.parentKey?.toString());
                addNodeAbove(node, siblingStaticBooleanAbove);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_BOOLEAN_ABOVE:
                const siblingMappedBooleanAbove = createMappedBooleanNode(node.parentKey?.toString());
                addNodeAbove(node, siblingMappedBooleanAbove);
                break;
                
            case ActionType.ADD_SIBLING_STATIC_NUMBER_ABOVE:
                const siblingStaticNumberAbove = createStaticNumberNode(node.parentKey?.toString());
                addNodeAbove(node, siblingStaticNumberAbove);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_NUMBER_ABOVE:
                const siblingMappedNumberAbove = createMappedNumberNode(node.parentKey?.toString());
                addNodeAbove(node, siblingMappedNumberAbove);
                break;

            case ActionType.ADD_SIBLING_SEQUENCE_NUMBER_ABOVE:
                const siblingSequenceNumberAbove = createSequenceNumberNode(node.parentKey?.toString());
                addNodeAbove(node, siblingSequenceNumberAbove);
                break;

            case ActionType.ADD_SIBLING_STATIC_TEXT_ABOVE:
                const siblingStaticTextAbove = createStaticTextNode(node.parentKey?.toString());
                addNodeAbove(node, siblingStaticTextAbove);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_TEXT_ABOVE:
                const siblingMappedTextAbove = createMappedTextNode(node.parentKey?.toString());
                addNodeAbove(node, siblingMappedTextAbove);
                break;

            case ActionType.ADD_SIBLING_SEQUENCE_TEXT_ABOVE:
                const siblingSequenceTextAbove = createSequenceTextNode(node.parentKey?.toString());
                addNodeAbove(node, siblingSequenceTextAbove);
                break;

            case ActionType.ADD_SIBLING_ATTRIBUTE_ABOVE:
                const siblingAttributeAbove = createAttribute(node.parentKey?.toString());
                addNodeAbove(node, siblingAttributeAbove);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE:
                const siblingMappedAtrributeAbove = createMappedAttribute(node.parentKey?.toString());
                addNodeAbove(node, siblingMappedAtrributeAbove);
                break;

            case ActionType.ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE:
                const siblingSequenceAtrributeAbove = createSequenceAttribute(node.parentKey?.toString());
                addNodeAbove(node, siblingSequenceAtrributeAbove);
                break;

            case ActionType.ADD_SIBLING_NODE_BELOW:
                const siblingNodeBelow = createNode(node.parentKey?.toString());
                addNodeBelow(node, siblingNodeBelow);
                break;

            case ActionType.ADD_SIBLING_CDATA_BELOW:
                const siblingCDataBelow = createCData(node.parentKey?.toString());
                addNodeBelow(node, siblingCDataBelow);
                break;

            case ActionType.ADD_SIBLING_ARRAY_NODE_BELOW:
                const siblingArrayNodeBelow = createArrayNode(node.parentKey?.toString());
                addNodeBelow(node, siblingArrayNodeBelow);
                break;

            case ActionType.ADD_SIBLING_PHANTOM_NODE_BELOW:
                const siblingPhantomNodeBelow = createPhantomNode(node.parentKey?.toString());
                addNodeBelow(node, siblingPhantomNodeBelow);
                break;

            case ActionType.ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW:
                const siblingPhantomArrayNodeBelow = createPhantomArrayNode(node.parentKey?.toString());
                addNodeBelow(node, siblingPhantomArrayNodeBelow);
                break;
            
            case ActionType.ADD_SIBLING_STATIC_BOOLEAN_BELOW:
                const siblingStaticBooleanBelow = createStaticBooleanNode(node.parentKey?.toString());
                addNodeBelow(node, siblingStaticBooleanBelow);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_BOOLEAN_BELOW:
                const siblingMappedBooleanBelow = createMappedBooleanNode(node.parentKey?.toString());
                addNodeBelow(node, siblingMappedBooleanBelow);
                break;

            case ActionType.ADD_SIBLING_STATIC_NUMBER_BELOW:
                const siblingStaticNumberBelow = createStaticNumberNode(node.parentKey?.toString());
                addNodeBelow(node, siblingStaticNumberBelow);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_NUMBER_BELOW:
                const siblingMappedNumberBelow = createMappedNumberNode(node.parentKey?.toString());
                addNodeBelow(node, siblingMappedNumberBelow);
                break;

            case ActionType.ADD_SIBLING_SEQUENCE_NUMBER_BELOW:
                const siblingSequenceNumberBelow = createSequenceNumberNode(node.parentKey?.toString());
                addNodeBelow(node, siblingSequenceNumberBelow);
                break;

            case ActionType.ADD_SIBLING_STATIC_TEXT_BELOW:
                const siblingStaticTextBelow = createStaticTextNode(node.parentKey?.toString());
                addNodeBelow(node, siblingStaticTextBelow);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_TEXT_BELOW:
                const siblingMappedTextBelow = createMappedTextNode(node.parentKey?.toString());
                addNodeBelow(node, siblingMappedTextBelow);
                break;

            case ActionType.ADD_SIBLING_ATTRIBUTE_BELOW:
                const siblingAttributeBelow = createAttribute(node.parentKey?.toString());
                addNodeBelow(node, siblingAttributeBelow);
                break;
            
            case ActionType.ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW:
                const siblingMappedAtrributeBelow = createMappedAttribute(node.parentKey?.toString());
                addNodeBelow(node, siblingMappedAtrributeBelow);
                break;

            case ActionType.ADD_SIBLING_SEQUENCE_TEXT_BELOW:
                const siblingSequenceTextBelow = createSequenceTextNode(node.parentKey?.toString());
                addNodeBelow(node, siblingSequenceTextBelow);
                break;

            case ActionType.ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW:
                const siblingSequenceAtrributeBelow = createMappedAttribute(node.parentKey?.toString());
                addNodeBelow(node, siblingSequenceAtrributeBelow);
                break;

            case ActionType.CHANGE_TO_NODE: 
                changeNodeType(node, NodeType.NODE);
                break;

            case ActionType.CHANGE_TO_CDATA: 
                changeNodeType(node, NodeType.CDATA);
                break;

            case ActionType.CHANGE_TO_ARRAY_NODE: 
                changeNodeType(node, NodeType.ARRAY_NODE);
                break;

            case ActionType.CHANGE_TO_PHANTOM_NODE: 
                changeNodeType(node, NodeType.PHANTOM_NODE);
                break;

            case ActionType.CHANGE_TO_PHANTOM_ARRAY_NODE: 
                changeNodeType(node, NodeType.PHANTOM_ARRAY_NODE);
                break;

            case ActionType.CHANGE_TO_STATIC_BOOLEAN: 
                changeNodeType(node, NodeType.STATIC_BOOLEAN);
                break;

            case ActionType.CHANGE_TO_MAPPED_BOOLEAN: 
                changeNodeType(node, NodeType.MAPPED_BOOLEAN);
                break;

            case ActionType.CHANGE_TO_STATIC_NUMBER: 
                changeNodeType(node, NodeType.STATIC_NUMBER);
                break;

            case ActionType.CHANGE_TO_MAPPED_NUMBER: 
                changeNodeType(node, NodeType.MAPPED_NUMBER);
                break;

            case ActionType.CHANGE_TO_SEQUENCE_NUMBER: 
                changeNodeType(node, NodeType.SEQUENCE_NUMBER);
                break;

            case ActionType.CHANGE_TO_STATIC_TEXT: 
                changeNodeType(node, NodeType.STATIC_TEXT);
                break;

            case ActionType.CHANGE_TO_MAPPED_TEXT: 
                changeNodeType(node, NodeType.MAPPED_TEXT);
                break;

            case ActionType.CHANGE_TO_SEQUENCE_TEXT: 
                changeNodeType(node, NodeType.SEQUENCE_TEXT);
                break;

            case ActionType.CHANGE_TO_ATTRIBUTE: 
                changeNodeType(node, NodeType.ATTRIBUTE);
                break;

            case ActionType.CHANGE_TO_MAPPED_ATTRIBUTE: 
                changeNodeType(node, NodeType.MAPPED_ATTRIBUTE);
                break;

            case ActionType.CHANGE_TO_SEQUENCE_ATTRIBUTE: 
                changeNodeType(node, NodeType.SEQUENCE_ATTRIBUTE);
                break;

            case ActionType.DELETE_NODE:
                setTreeData((tree) => {
                    let clonedTree = [...tree];
                    let parentNode: TdiNodeModel = findParentFromTree(node.parentKey, clonedTree);
                    if (parentNode) {
                        let children = [...parentNode.children];
                        const ndx = children.findIndex((child) => child.key.toString() === node.key.toString());
                        children.splice(ndx, 1);
                        parentNode.children = children;
                        return clonedTree;
                    } else {
                        let clonedTree = [...tree];
                        const ndx = clonedTree.findIndex((child) => child.key.toString() === node.key.toString());
                        clonedTree.splice(ndx, 1);
                        return clonedTree;
                    }
                });
                break;
        }

        if (!edited) setEdited(true);
    }

    const nodeDropDown = (node: TdiNodeModel) => {

        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <FaElementor size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const cdataDropDown = (node: TdiNodeModel) => {

        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: cdataItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RxTextAlignJustify size={18} color={'gray'}/>
                        <Input defaultValue={'<![CDATA['} className="input-item" disabled/>
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const phantomNodeDropDown = (node: TdiNodeModel) => {

        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <FaElementor size={18} color={'gray'}/>
                        <Input defaultValue={'phantom node'} className="input-item" disabled/>
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const arrayNodeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <MdOutlineDataArray size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const phantomArrayNodeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <MdOutlineDataArray size={18} color={'gray'}/>
                        <Input defaultValue={'phantom array'} className="input-item" disabled/>
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const staticBooleanDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RxComponentBoolean size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const mappedBooleanDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <VscSymbolBoolean size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                        <Button type="primary" onClick={() => openConverter(node)}>
                            Converter
                        </Button>
                        {node.required && <span style={{color: 'red'}}>required</span>}
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const sequenceNumberDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RxText size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const staticNumberDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RiNumber1 size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const mappedNumberDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <TbHexagonNumber1Filled size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                        <Button type="primary" onClick={() => openConverter(node)}>
                            Converter
                        </Button>
                        {node.required && <span style={{color: 'red'}}>required</span>}
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const sequenceTextDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RxText size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const staticTextDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RxText size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const mappedTextDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: leafNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <FaHashnode size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                        <Button type="primary" onClick={() => openConverter(node)}>
                            Converter
                        </Button>
                        {node.required && <span style={{color: 'red'}}>required</span>}
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const attributeDropDown = (node: TdiNodeModel) => {
       return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: attributeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <FaCircle size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const mappedAttributeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: attributeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <FaCircleDot size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                        <Button type="primary" onClick={() => openConverter(node)}>
                            Converter
                        </Button>
                        {node.required && <span style={{color: 'red'}}>required</span>}
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const sequenceAttributeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: attributeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <FaCircleExclamation size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    /**
     * Creates a Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let childNode: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.NODE,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        childNode.title = nodeDropDown(childNode);
        return childNode;
    }

    /**
     * Creates a Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createCData = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let childNode: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.CDATA,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        childNode.title = cdataDropDown(childNode);
        return childNode;
    }

    /**
     * Creates a Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createPhantomNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let childNode: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.PHANTOM_NODE,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        childNode.title = nodeDropDown(childNode);
        return childNode;
    }

    /**
     * Creates an Array Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createArrayNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.ARRAY_NODE,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = arrayNodeDropDown(node);
        return node;
    }

    /**
     * Creates an Array Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createPhantomArrayNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.PHANTOM_ARRAY_NODE,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = phantomArrayNodeDropDown(node);
        return node;
    }
    
    const openConverter = (node:TdiNodeModel) => {
        setCurrentNodeModel(node);
        setConverterOpen(true);
    }

    /**
     * Creates a Sequence Number Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createSequenceNumberNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.SEQUENCE_NUMBER,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = sequenceNumberDropDown(node);
        return node;
    }

    /**
     * Creates a Static Boolean Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createStaticBooleanNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.STATIC_BOOLEAN,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = staticBooleanDropDown(node);
        return node;
    }

    /**
     * Creates a Static Text Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createStaticNumberNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.STATIC_NUMBER,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = staticNumberDropDown(node);
        return node;
    }

    /**
     * Creates a Mapped Number Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createMappedBooleanNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.MAPPED_BOOLEAN,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: dataMapModel?.required,
            children: [],
            converterType: dataMapModel?.converterType || 'NONE',
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = mappedBooleanDropDown(node);
        return node;
    }

    /**
     * Creates a Mapped Number Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createMappedNumberNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.MAPPED_NUMBER,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: dataMapModel?.required,
            children: [],
            converterType: dataMapModel?.converterType || 'NONE',
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = mappedNumberDropDown(node);
        return node;
    }

    /**
     * Creates a Mapped Text Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createMappedTextNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.MAPPED_TEXT,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: dataMapModel?.required,
            children: [],
            converterType: dataMapModel?.converterType || 'NONE',
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = mappedTextDropDown(node);
        return node;
    }

    /**
     * Creates a Mapped Text Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createSequenceTextNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.SEQUENCE_TEXT,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = sequenceTextDropDown(node);
        return node;
    }

    /**
     * Creates a Mapped Text Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createStaticTextNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.STATIC_TEXT,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = staticTextDropDown(node);
        return node;
    }

    /**
     * changes the node type to {type}.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const changeNodeType = (node: TdiNodeModel, nodeType: string): TdiNodeModel => {

        node.type = nodeType;

        switch (nodeType) {
            case NodeType.NODE: node.title = nodeDropDown(node); break;
            case NodeType.ARRAY_NODE: node.title = arrayNodeDropDown(node); break;
            case NodeType.CDATA: node.title = cdataDropDown(node); break;
            case NodeType.PHANTOM_NODE: node.title = phantomNodeDropDown(node); break;
            case NodeType.PHANTOM_ARRAY_NODE: node.title = phantomArrayNodeDropDown(node); break;
            case NodeType.STATIC_BOOLEAN: node.title = staticBooleanDropDown(node); break;
            case NodeType.MAPPED_BOOLEAN: node.title = mappedBooleanDropDown(node); break;
            case NodeType.STATIC_NUMBER: node.title = staticNumberDropDown(node); break;
            case NodeType.SEQUENCE_NUMBER: node.title = sequenceNumberDropDown(node); break;
            case NodeType.MAPPED_NUMBER: node.title = mappedNumberDropDown(node); break;
            case NodeType.STATIC_TEXT: node.title = staticTextDropDown(node); break;
            case NodeType.SEQUENCE_TEXT: node.title = sequenceTextDropDown(node); break;
            case NodeType.MAPPED_TEXT: node.title = mappedTextDropDown(node); break;
            case NodeType.ATTRIBUTE: node.title = attributeDropDown(node); break;
            case NodeType.MAPPED_ATTRIBUTE: node.title = mappedAttributeDropDown(node); break;
            case NodeType.SEQUENCE_ATTRIBUTE: node.title = sequenceAttributeDropDown(node); break;
        }
        
        setTreeData((tree) => {
            let clonedTree = [...tree];
            let parentNode: TdiNodeModel = findParentFromTree(node.parentKey, clonedTree);
            if (parentNode) {
                let children = [...parentNode.children];
                const ndx = children.findIndex((child) => child.key.toString() === node.key.toString());
                children.splice(ndx, 1, node);
                parentNode.children = children;
                return clonedTree;
            } else {
                let clonedTree = [...tree];
                const ndx = clonedTree.findIndex((child) => child.key.toString() === node.key.toString());
                clonedTree.splice(ndx, 1, node);
                return clonedTree;
            }
        });
        
        return node;
    }

    /**
     * Creates an Attribute.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createAttribute = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.ATTRIBUTE,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: false,
            children: [],
            enums: [],
        }

        node.title = attributeDropDown(node);

        return node;
    }

    /**
     * Creates an Attribute.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createMappedAttribute = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.MAPPED_ATTRIBUTE,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: false,
            converterType: dataMapModel?.converterType || 'NONE',
            children: [],
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = mappedAttributeDropDown(node);

        return node;
    }

    /**
     * Creates an Attribute.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createSequenceAttribute = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: NodeType.SEQUENCE_ATTRIBUTE,
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: false,
            converterType: dataMapModel?.converterType || 'NONE',
            children: [],
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = sequenceAttributeDropDown(node);

        return node;
    }

    /**
     * Adds a root node.
     * Called when the button at the top left for adding a root node is clicked.
     */
    const addRootNode = () => {
        const nodeModel = createNode();
        const data = [...treeData, nodeModel];
        setTreeData(data);
        if (!edited) setEdited(true);
    }

    /**
     * Adds an root array node.
     * Called when the button at the top left for adding a root node is clicked.
     */
    const addRootArrayNode = () => {
        const nodeModel = createArrayNode();
        const data = [...treeData, nodeModel];
        setTreeData(data);
        if (!edited) setEdited(true);
    }

    /**
     * Previews the output generated by this mapping
     */
    const previewOutput = () => {
        setPreviewOpen(true);
    }

    /**
     * Saves the data mapping.
     */
    const saveDataMapping = async () => {

        /**
         * Recursively traverses the tree to convert the data into a format the the server
         * understands and can save.
         * 
         * @param nodes 
         * @param parent 
         * @returns 
         */
        const buildDataMapModel = (nodes: TdiNodeModel[], parent?: DataMapModel): DataMapModel[] => {

            const children = nodes.map((item: TdiNodeModel) => {
                let dataMap: DataMapModel = {
                    key: item?.key.toString(),
                    parentKey: item?.parentKey,
                    type: item.type,
                    source: item.source,
                    target: item.target,
                    required: item.required,
                    converterType: item.converterType,
                    script: item.script,
                    enums: item.enums?.map((item: DataMapModel) => {
                        return {source: item.source, target: item.target};
                    })
                }
                dataMap.children = buildDataMapModel(item.children, dataMap);
                return dataMap;
            });
            return children;
        }

        const children = buildDataMapModel(treeData);
        const dataMap = {
            clientId, 
            children
        }

        try {
            
            await saveDataMap(dataMap).unwrap();
            setEdited(false);
            notification.success({
                message: "Data Mapping Saved!",
                description: `The data mapping configuration has been created.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Saving Data Mapping Failed!",
                description: `An error occurred while saving the data mapping configuration.`
            });
        }
    }

    const saveConverter = (values: any) => {
        
        if (currentNodeModel) {
            const data = [...treeData];
            let node = findNodeFromTree(currentNodeModel, data);
            node.required = values.required;
            node.converterType = values.converterType;
            node.enums = values.enums?.map((item: any) => {
                return {source: item.source, target: item.target}
            });
            node.source = values.source;
            node.script = values.script;
            setTreeData(data);
        }
        if (!edited) setEdited(true);
        setConverterOpen(false);
    }

    return <div className="data-mapping">
        <div className="data-mapping-header">
            <h1>Data Mapping - {client?.name}</h1>
            <span style={{ display: 'flex', gap: '.5rem' }}>
                <Button type="primary" onClick={addRootNode} icon={<FaElementor />}>
                    Add Root Node
                </Button>
                <Button type="primary" onClick={addRootArrayNode} icon={<MdOutlineDataArray />}>
                    Add Root Array Node
                </Button>
                <span style={{width: '3rem'}}></span>
                <Button type="primary" onClick={previewOutput} icon={<FaSearch />}>
                    Preview
                </Button>
            </span>
        </div>
        {treeData.length !== 0 && (
            <div className="data-mapping-tree-content">
                <Tree
                    showIcon
                    defaultExpandAll
                    switcherIcon={<AiOutlineDown />}
                    treeData={treeData}
                />
            </div>
        )}
        
        {loading && 
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32}}>
                <Spin tip="Loading" size={"large"}/>
            </div>
        }

        <div className="data-mapping-control">
            <Button
                type="primary"
                onClick={saveDataMapping}
                disabled={!edited}
            >
                Save Data Mapping
            </Button>
        </div>

        <ConverterModal
            open={isConverterOpen}
            onCancel={() => setConverterOpen(false)}
            node={currentNodeModel}
            onOk={saveConverter}
        />

        <PreviewModal
            client={client}
            open={isPreviewOpen}
            onCancel={() => setPreviewOpen(false)}
        />
    </div>

}

export default DataMappingPage;
